import axios from 'axios';
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MdBlock, MdDelete, MdKeyboardArrowLeft, MdKeyboardArrowRight, MdOutlineCircle, MdOutlineClose } from 'react-icons/md';
import { Tooltip } from 'react-tooltip';
import { IoMdEye } from 'react-icons/io';
import { RiPencilFill } from 'react-icons/ri';
import ReactPaginate from 'react-paginate';
import { ToastContainer, toast } from 'react-toastify';

import constant from '../../utils/Constant';
import config from '../../config/config';
import Loader from '../partials/Loader';

const {
    ALL_VENDORS_END_POINT,
    BOOLEAN_FALSE: falseValue,
    EMPTY_STRING: emptyString,
    BOOLEAN_TRUE: trueValue,
    VENDOR_STATUS_END_POINT,
    DELETE_VENDOR_END_POINT,
    REQUESTED_RECORD,
    NULL,
    ZERO,
    TWO,
    ONE,
    STATUS_200,
    STATUS_401,
    LOGOUT,
    THREE
} = constant;

const AllQuotation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPage = location.state?.page || ONE;

    const [data, setData] = useState([]);
    const [status, setStatus] = useState({});
    const [page, setPage] = useState(currentPage);
    const [totalPages, setTotalPages] = useState(ONE);
    const [openModal, setOpenModal] = useState(falseValue);
    const [showConfirmation, setShowConfirmation] = useState(falseValue);
    const [confirmationModal, setConfirmationModal] = useState(falseValue);
    const [loading, setLoading] = useState(falseValue);
    const [disableButton, setDisableButton] = useState(falseValue);
    const [vendorID, setVendorID] = useState(emptyString);
    const [vendorStat, setVendorStat] = useState(THREE);
    const confirmationRef = useRef(NULL);

    // * get token from local storage
    const token = localStorage.getItem('vendor_super_token');

    // * execute all vendor request api
    const executeAllVendorApi = useCallback(async () => {
        try {
            const apiUrl = `${config.appBaseUrl + ALL_VENDORS_END_POINT}/${vendorStat}?page=${page}`;

            // * execute get request
            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // * api response
            const result = response.data;

            // * if response is success
            if (result.code === STATUS_200 && result.status) {
                setData(result.data.data);
                setPage(page);
                setTotalPages(result.data.last_page);
            }
        } catch (error) {
            // * if token is expired
            if (error?.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
            toast.error(error);
        }
    }, [navigate, token, page, vendorStat]);

    // * execute all vendor request api
    useEffect(() => {
        executeAllVendorApi();
    }, [executeAllVendorApi, token, page, vendorStat]);

    // * handle page change
    const handlePageChange = (selectedPage) => {
        const newPage = selectedPage.selected + ONE;
        setPage(newPage);
        navigate('/vendors/all', { state: { page: newPage } });
    };

    //* disable scroll
    useEffect(() => {
        if (openModal || confirmationModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [openModal, confirmationModal]);

    //* close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (confirmationRef.current && !confirmationRef.current.contains(event.target)) {
                setOpenModal(falseValue);
                setConfirmationModal(falseValue);
            }
        };

        if (openModal || confirmationModal) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openModal, setOpenModal, confirmationModal, setConfirmationModal]);

    //* trap focus within modal when open
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Tab' && (openModal || confirmationModal)) {
                const focusableElements = confirmationRef.current.querySelectorAll('button, input, select, textarea, a');
                if (focusableElements.length === 0) return;

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                //* prevent tabbing outside the modal
                if (event.shiftKey) {
                    if (document.activeElement === firstElement) {
                        lastElement.focus();
                        event.preventDefault();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        firstElement.focus();
                        event.preventDefault();
                    }
                }
            }
        };

        //* add event listener
        if (openModal || confirmationModal) {
            document.addEventListener('keydown', handleKeyDown);
        } else {
            document.removeEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [openModal, confirmationModal]);

    // * show confirmation model
    const showConfirmationModel = (params) => {
        const { id, status } = params;
        setStatus({
            id: id,
            status: status
        })
        setShowConfirmation(trueValue);
    }

    // * execute visible vendor api
    const executeVendorStatusApi = async () => {
        setLoading(trueValue)
        setDisableButton(trueValue)

        const sendRequest = {
            vendor_id: status.id,
            status: status.status
        }

        try {
            const apiUrl = config.appBaseUrl + VENDOR_STATUS_END_POINT;

            // * execute patch request
            const response = await axios.post(apiUrl, sendRequest,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // * if response is success then set data
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                executeAllVendorApi();
                toast.success(result.message);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            // * if token is expired
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
            toast.error(error);
        } finally {
            setLoading(falseValue);
            setDisableButton(falseValue);
        }
        setShowConfirmation(falseValue);
    };

    // * execute delete vendor api
    const executeDeleteMobileApi = async () => {

        setLoading(trueValue);
        setDisableButton(trueValue);

        try {
            const apiUrl = `${config.appBaseUrl + DELETE_VENDOR_END_POINT}/${vendorID}`;

            //* post request
            const response = await axios.get(apiUrl,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            //* response success
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                toast.success(result.message);
                executeAllVendorApi();
                setConfirmationModal(falseValue);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            toast.error(error?.response?.data?.message);

            // * if user is unauthorized
            if (error?.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
        } finally {
            setDisableButton(falseValue);
            setLoading(falseValue);
        }
    };

    return (
        <>
            <ToastContainer autoClose={700} position='top-center' />

            {/* //* status update model */}
            {showConfirmation && (
                <>
                    <div className='modal-backdrop fade show'></div>
                    <div
                        id='user_logout_modal'
                        className='modal fade show'
                        style={{ display: 'block' }}
                    >
                        <div className='modal-dialog'>
                            <div className='modal-content' ref={confirmationRef}>
                                <div className='modal-body'>
                                    <p className='mb-0'>Are you sure you want to update status?</p>
                                </div>
                                <div className='modal-footer me-2 border-top-0'>
                                    <button
                                        type='button'
                                        className='btn btn-danger text-white'
                                        data-dismiss='modal'
                                        onClick={() => setShowConfirmation(falseValue)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='button'
                                        className='btn btn-success text-white'
                                        onClick={executeVendorStatusApi}
                                        disabled={disableButton}
                                    >
                                        {!loading ? 'Confirm' : <Loader />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* //* delete vendor model */}
            {confirmationModal && (
                <>
                    <div className='modal-backdrop fade show'></div>
                    <div
                        id='user_logout_modal'
                        className='modal fade show'
                        style={{ display: 'block' }}
                    >
                        <div className='modal-dialog'>
                            <div className='modal-content' ref={confirmationRef}>
                                <div className='modal-body'>
                                    <p className='mb-0'>Are you sure you want to delete this vendor?</p>
                                </div>
                                <div className='modal-footer me-2 border-top-0'>
                                    <button
                                        type='button'
                                        className='btn btn-danger text-white'
                                        data-dismiss='modal'
                                        onClick={() => setConfirmationModal(falseValue)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='button'
                                        className='btn btn-success text-white'
                                        onClick={executeDeleteMobileApi}
                                        disabled={disableButton}
                                    >
                                        {!loading ? 'Confirm' : <Loader />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className='page-content'>
                <div className='container-fluid'>
                    {/* //* page heading */}
                    <div className='row'>
                        <div className='col-12'>
                            <div className='page-title-box d-flex  align-items-start align-items-sm-center justify-content-between gap-2'>
                                <h4 className='mb-2 mb-sm-0'>Vendors</h4>
                                <div className='page-title-right'>
                                    <ol className='breadcrumb m-0'>
                                        <li className='breadcrumb-item'>
                                            <Link to='/dashboard'>Dashboard</Link>
                                        </li>
                                        <li className='breadcrumb-item active'>Vendors</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* end page title */}
                    <div className='row'>
                        <div className='col-lg-12'>
                            <div className='card'>
                                <div className='card-body'>
                                    {/* //* header buttons */}
                                    <div
                                        className='d-flex align-items-center justify-content-between mb-3'
                                        style={{ width: '100%' }}
                                    >
                                        <div className='header-input'>
                                            <select name='options' id='optionSelect' className='form-select' value={vendorStat} onChange={(e) => setVendorStat(e.target.value)}>
                                                <option value='3'>All</option>
                                                <option value='0'>Pending</option>
                                                <option value='2'>Rejected</option>
                                                <option value='1'>Approved</option>
                                            </select>
                                        </div>
                                        <button type='button' className='btn btn-success' onClick={() => navigate('/vendors/create')}>
                                            Create
                                        </button>
                                    </div>

                                    <div className='table-responsive'>
                                        <table className='w-100 text-sm text-left border-collapse custom-stripe-table '>
                                            <thead className='bg-gray-200  text-center'>
                                                <tr>
                                                    <th className='px-4 py-2'>SN</th>
                                                    <th className='px-4 py-2'>Category</th>
                                                    <th className='px-4 py-2'>Business Name</th>
                                                    <th className='px-4 py-2'>Email</th> 
                                                    <th className='px-4 py-2'>Status</th>
                                                    <th className='px-4 py-2'>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    data.length > ZERO ?
                                                        data.map((vendor, index) => (
                                                            <tr className='bg-white hover:bg-gray-100' key={vendor.id}>
                                                                <td className='px-4 py-2'>{((page - ONE) * REQUESTED_RECORD) + (index + ONE)}</td>
                                                                <td className='px-4 py-2'>{vendor.category == ONE ? 'Mobile Bar' : 'Caterer'}</td>
                                                                <td className='px-4 py-2 text-nowrap'>{vendor.name}</td>
                                                                <td className='px-4 py-2 text-nowrap'>{vendor.email}</td> 
                                                                <td className='px-4 py-2'>
                                                                    <span className={`badge ${vendor.status == ZERO ? 'badge-primary-lighten' : vendor.status == ONE ? 'badge-success-lighten' : 'badge-danger-lighten'}`}>
                                                                        {vendor.status == ZERO ? 'PENDING' : vendor.status == ONE ? 'APPROVED' : 'REJECTED'}
                                                                    </span>
                                                                </td>
                                                                <td className='px-4 py-2'>
                                                                    <div className='d-flex align-items-center justify-content-center'>
                                                                        {
                                                                            (vendor.status == ZERO || vendor.status == TWO) &&
                                                                            <>
                                                                                <MdOutlineCircle
                                                                                    size={20}
                                                                                    data-tooltip-id='approve' data-tooltip-content='Approve'
                                                                                    className=' vendor-icon pointer user-status fs-5 me-2' style={{ cursor: 'pointer', color: 'green' }}
                                                                                    onClick={() => showConfirmationModel({ id: vendor.id, status: ONE })}
                                                                                />
                                                                                <Tooltip id='approve' place='bottom' effect='solid' />
                                                                            </>
                                                                        }
                                                                        {
                                                                            vendor.status == ONE && (
                                                                                <>
                                                                                    <MdBlock
                                                                                        size={20}
                                                                                        data-tooltip-id='reject' data-tooltip-content='Reject'
                                                                                        className=' vendor-icon pointer user-status fs-5 me-2' style={{ cursor: 'pointer', color: 'red' }}
                                                                                        onClick={() => showConfirmationModel({ id: vendor.id, status: TWO })}
                                                                                    />
                                                                                    <Tooltip id='reject' place='bottom' effect='solid' />
                                                                                </>
                                                                            )
                                                                        }

                                                                        <IoMdEye
                                                                            size={20}
                                                                            data-tooltip-id='update_vendor' data-tooltip-content='View Vendor'
                                                                            className=' text-primary vendor-icon pointer user-status me-2'
                                                                            onClick={() => navigate('/vendors/view', {
                                                                                state: {
                                                                                    vendor_id: vendor.id
                                                                                }
                                                                            })}
                                                                        />
                                                                        <Tooltip id='update_vendor' place='bottom' effect='solid' />

                                                                        <RiPencilFill
                                                                            size={20}
                                                                            data-tooltip-id='update_vendor' data-tooltip-content='Update Vendor'
                                                                            className=' text-primary vendor-icon pointer user-status me-2'
                                                                            onClick={() => navigate('/vendors/update', {
                                                                                state: {
                                                                                    vendor_id: vendor.id
                                                                                }
                                                                            })}
                                                                        />
                                                                        <Tooltip id='update_vendor' place='bottom' effect='solid' />

                                                                        <MdDelete
                                                                            size={20}
                                                                            data-tooltip-id='delele_vendor' data-tooltip-content='Delete Vendor'
                                                                            className=' text-danger vendor-icon pointer user-status'
                                                                            onClick={() => {
                                                                                setConfirmationModal(trueValue);
                                                                                setVendorID(vendor.id)
                                                                            }}
                                                                        />
                                                                        <Tooltip id='delele_vendor' place='bottom' effect='solid' />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                        :
                                                        <tr>
                                                            <td colSpan={7} className='text-center'>No data found!</td>
                                                        </tr>
                                                }
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* //* pagination */}
                                    {
                                        data.length > ZERO &&
                                        <div className='float-end'>
                                            <ReactPaginate
                                                className='pagination'
                                                onPageChange={handlePageChange}
                                                pageCount={totalPages}
                                                forcePage={(totalPages > ZERO) && (page <= totalPages) ? (page - ONE) : ZERO}
                                                previousLabel={<MdKeyboardArrowLeft className='fs-4' />}
                                                nextLabel={<MdKeyboardArrowRight className='fs-4' />}
                                                marginPagesDisplayed={TWO}
                                                pageRangeDisplayed={TWO}
                                                breakLabel={'..'}
                                                breakLinkClassName={'disabled-break'}
                                                containerClassName={'pagination-container'}
                                                activeClassName={'pagination-active-page'}
                                            />
                                        </div>
                                    }

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default AllQuotation;